import { Badge, Button, Popover } from 'antd'
import React, { useState } from 'react'
import useQueryParam from '../../../../../../hooks/useQueryParam'
import { FileTextOutlined, FlagFilled, FlagOutlined } from '@ant-design/icons'
import { useThemeColors } from '../../../../../../hooks/useThemeColors'
import { useUpdateQuestionAnswerEscalatedMutation } from '../../../../../../store/apis/submission.api'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import { FLAGGED_PARAM, SUBMISSION_SEARCH_PARAM } from '../../../../../../constants/query-params'
import { useParams } from 'react-router'
import { useCloseAndNavigateToDashboard, useSelectNextEscalatedQuestionRubric } from '../../../../../../store/apis/utils/hooks/text-submission.hooks'
import { ExplainabilityStatuses } from '../../../../../../constants/explainability-statuses'
import FlagNotePopoverContent from './FlagNotePopoverContent'

const FlagToggle = ({ answerId, questionId, questionRubricId, submissionId, flagged, existingNote = '' }) => {
    const colors = useThemeColors()
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [isNewFlag, setIsNewFlag] = useState(false) // Track if we just flagged vs viewing existing
    const search = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const versionId = useVersionId()
    const { token } = useParams()

    const isFlagged = flagged
    const hasNote = Boolean(existingNote?.trim())

    const [updateQuestionAnswerEscalated, { isLoading }] = useUpdateQuestionAnswerEscalatedMutation()

    const isInEscalatedMode = useQueryParam(FLAGGED_PARAM) === 'true'
    const goToNext = useSelectNextEscalatedQuestionRubric(answerId, submissionId)
    const closeAndNavigateToDashboard = useCloseAndNavigateToDashboard()

    const handleFlagSubmission = async () => {
        await updateQuestionAnswerEscalated({
            id: answerId,
            questionId,
            questionRubricId,
            submissionId,
            escalated: ExplainabilityStatuses.MANUAL_UNRESOLVED,
            search,
            versionId,
            token,
        })
        // After flagging, show popover to ask for note (mark as new flag)
        setIsNewFlag(true)
        setPopoverOpen(true)
    }

    const handleClearFlag = async () => {
        setPopoverOpen(false)
        
        await updateQuestionAnswerEscalated({
            id: answerId,
            questionId,
            questionRubricId,
            submissionId,
            escalated: ExplainabilityStatuses.MANUAL_RESOLVED,
            // Note: We don't clear the note - it persists after unflagging
            search,
            versionId,
            token,
        })
        
        await closeAndNavigateToDashboard()

        if (!isInEscalatedMode) {
            goToNext()
        }
    }

    const handleDeleteNote = async () => {
        await updateQuestionAnswerEscalated({
            id: answerId,
            questionId,
            questionRubricId,
            submissionId,
            escalated: ExplainabilityStatuses.MANUAL_RESOLVED,
            note: '', // Explicitly clear the note
            search,
            versionId,
            token,
        })
        setPopoverOpen(false)
    }

    const handleReFlag = async () => {
        await updateQuestionAnswerEscalated({
            id: answerId,
            questionId,
            questionRubricId,
            submissionId,
            escalated: ExplainabilityStatuses.MANUAL_UNRESOLVED,
            search,
            versionId,
            token,
        })
        setPopoverOpen(false)
    }

    const handleClick = (e) => {
        e.stopPropagation()
        
        if (isFlagged) {
            // If already flagged, show popover to view/edit note or clear flag
            setIsNewFlag(false)
            setPopoverOpen(true)
        } else if (hasNote) {
            // If unflagged but has note, show popover to view note
            setIsNewFlag(false)
            setPopoverOpen(true)
        } else {
            // If not flagged and no note, flag immediately and show note popover
            handleFlagSubmission()
        }
    }

    const handleClosePopover = () => {
        setPopoverOpen(false)
        setIsNewFlag(false)
    }

    const popoverContent = (
        <FlagNotePopoverContent
            answerId={answerId}
            questionRubricId={questionRubricId}
            submissionId={submissionId}
            onSaveNote={handleClosePopover}
            onCancel={handleClosePopover}
            onClearFlag={handleClearFlag}
            onDeleteNote={handleDeleteNote}
            onReFlag={handleReFlag}
            existingNote={existingNote}
            isNewFlag={isNewFlag}
            isFlagged={isFlagged}
            isClearing={isLoading}
        />
    )

    // Show note badge for both flagged AND unflagged states if note exists
    const flagIcon = hasNote ? (
        <Badge 
            count={<FileTextOutlined style={{ fontSize: 10 }} />}
            size="small"
        >
            {isFlagged ? <FlagFilled style={{ color: colors.error }} /> : <FlagOutlined />}
        </Badge>
    ) : isFlagged ? (
        <FlagFilled style={{ color: colors.error }} />
    ) : (
        <FlagOutlined />
    )

    return (
        <Popover
            content={popoverContent}
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            placement="bottomRight"
            title={null}
            destroyTooltipOnHide
        >
            <Button
                type='text'
                icon={flagIcon}
                onClick={handleClick}
                title={isFlagged ? 'View/edit flag note' : hasNote ? 'View note' : 'Flag for review'}
                loading={isLoading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                data-test='flag-submission-button'
            />
        </Popover>
    )
}

export default FlagToggle

